import type { Session } from '@supabase/supabase-js';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { AuthState, AuthUser } from './context';
import { loadSupabase } from './supabase';
import { setAuthHeadersProvider } from '@/api/auth-headers';
import type { AppConfig } from '@/config';

function userOf(session: Session | null): AuthUser | null {
  if (!session) return null;
  const email = session.user.email ?? '';
  const meta = session.user.user_metadata as { full_name?: unknown; name?: unknown } | undefined;
  const name = [meta?.full_name, meta?.name].find((n): n is string => typeof n === 'string');
  return { email, name: name ?? email };
}

/**
 * Supabase email sign-in: the email carries a one-time code (typed on the login page, any device)
 * and a magic link (same browser only, because of PKCE). The API receives the access token as a
 * bearer token.
 */
export function useSupabaseAuth(cfg: AppConfig, active: boolean): AuthState | null {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(active);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!active) return;
    let unsubscribe: (() => void) | undefined;
    let cancelled = false;
    loadSupabase(cfg.supabaseUrl, cfg.supabaseAnonKey)
      .then(async (sb) => {
        setAuthHeadersProvider(async (): Promise<Record<string, string>> => {
          const { data } = await sb.auth.getSession();
          const token = data.session?.access_token;
          return token ? { Authorization: `Bearer ${token}` } : {};
        });
        const { data: sub } = sb.auth.onAuthStateChange((_event, s) => setSession(s));
        unsubscribe = () => sub.subscription.unsubscribe();
        const { data, error: e } = await sb.auth.getSession();
        if (cancelled) return;
        if (e) setError(e.message);
        setSession(data.session);
      })
      .catch((e: unknown) => !cancelled && setError(e instanceof Error ? e.message : String(e)))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
      unsubscribe?.();
    };
  }, [active, cfg.supabaseUrl, cfg.supabaseAnonKey]);

  const signInWithEmail = useCallback(
    async (email: string, next = '/cases') => {
      const sb = await loadSupabase(cfg.supabaseUrl, cfg.supabaseAnonKey);
      const redirect = new URL('/auth/callback', window.location.origin);
      redirect.searchParams.set('next', next);
      const { error: e } = await sb.auth.signInWithOtp({
        email,
        options: { emailRedirectTo: redirect.toString(), shouldCreateUser: false },
      });
      if (e) throw new Error(e.message);
    },
    [cfg.supabaseUrl, cfg.supabaseAnonKey],
  );

  const verifyEmailCode = useCallback(
    async (email: string, code: string) => {
      const sb = await loadSupabase(cfg.supabaseUrl, cfg.supabaseAnonKey);
      const { error: e } = await sb.auth.verifyOtp({ email, token: code, type: 'email' });
      if (e) throw new Error(e.message);
    },
    [cfg.supabaseUrl, cfg.supabaseAnonKey],
  );

  const signOut = useCallback(async () => {
    const sb = await loadSupabase(cfg.supabaseUrl, cfg.supabaseAnonKey);
    await sb.auth.signOut();
  }, [cfg.supabaseUrl, cfg.supabaseAnonKey]);

  return useMemo<AuthState | null>(
    () =>
      active
        ? {
            mode: 'supabase',
            loading,
            user: userOf(session),
            signInWithEmail,
            verifyEmailCode,
            signOut,
            error,
          }
        : null,
    [active, loading, session, signInWithEmail, verifyEmailCode, signOut, error],
  );
}
